import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

export function useFeatureFlag(featureKey: string) {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id ?? null);
    });
  }, []);

  return useQuery({
    queryKey: ['feature-flag', featureKey, userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('is_feature_enabled', {
          _feature_key: featureKey,
          _user_id: userId
        });
      if (error) throw error;
      return data as boolean;
    },
    staleTime: 60000,
    enabled: featureKey !== undefined
  });
}

export function useFeatureFlags(featureKeys: string[]) {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id ?? null);
    });
  }, []);

  return useQuery({
    queryKey: ['feature-flags-batch', featureKeys.join(','), userId],
    queryFn: async () => {
      const results: Record<string, boolean> = {};
      await Promise.all(
        featureKeys.map(async (key) => {
          const { data } = await supabase.rpc('is_feature_enabled', {
            _feature_key: key,
            _user_id: userId
          });
          results[key] = data ?? false;
        })
      );
      return results;
    },
    staleTime: 60000,
    enabled: featureKeys.length > 0
  });
}

// Helper to check multiple flags at once with loading state
export function useAllFeatureFlags() {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id ?? null);
    });
  }, []);

  return useQuery({
    queryKey: ['all-feature-flags', userId],
    queryFn: async () => {
      const { data: flags, error } = await supabase
        .from('feature_flags')
        .select('key, status, scope, rollout_percentage');
      
      if (error) throw error;

      const results: Record<string, boolean> = {};
      await Promise.all(
        (flags || []).map(async (flag) => {
          const { data } = await supabase.rpc('is_feature_enabled', {
            _feature_key: flag.key,
            _user_id: userId
          });
          results[flag.key] = data ?? false;
        })
      );
      return results;
    },
    staleTime: 60000
  });
}
